import { defineStore } from 'pinia'
import { ref } from 'vue'
import type { FormDocument } from '@verifiedit/form-generator'

/**
 * The logged-in user's employee photo as this app can currently show it: the `{uuid, status}`
 * reference login reported, plus the bytes fetched for it as an object url.
 *
 * Deliberately **not** persisted, unlike the rest of the user's state. Photo bytes written to a
 * shared site device's `localStorage` are the same leak B40 closed on the wire, one hop later and
 * at rest — so the reference lives in the persisted user store and the bytes only ever live here,
 * for as long as the tab does. A reload re-fetches.
 */
export const useEmployeePhotoStore = defineStore('employeePhoto', () => {
  /** state */
  const photo = ref<FormDocument | null>(null)

  /** actions */
  function setPhoto(value: FormDocument | null): void {
    releasePhotoContent()
    photo.value = value
  }

  function resetPhoto(): void {
    setPhoto(null)
  }

  /**
   * The url the content fetch made is this store's to revoke: nothing else holds a reference to
   * it, and nothing else knows when it stops being the photo.
   */
  function releasePhotoContent(): void {
    const content = photo.value?.content

    if (content !== undefined && content !== null) {
      URL.revokeObjectURL(content)
    }
  }

  return {
    photo,
    resetPhoto,
    setPhoto,
  }
})
